/**
 * Barra de filtros de la pestana Stock de Inventario (INV-01): buscador,
 * estado del stock, categoria y orden. Mismos controles y mismo aspecto que la
 * tabla de productos; el que esta filtrando se pinta en turquesa.
 *
 * El buscador va dentro de la barra y no en la cabecera: la pantalla tiene dos
 * pestanas y cada una busca otra cosa (productos aca, movimientos alla).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { OpcionesFiltroProductos, OrdenProductosAdmin } from '@bandu/compartido';

import {
  conCambio,
  estadoDe,
  ORDEN_STOCK_POR_DEFECTO,
  ORDENES_STOCK,
  rangoDeEstado,
} from '../../utilidades/filtros-inventario';
import type { EstadoStock, FiltrosStock as Filtros } from '../../utilidades/filtros-inventario';
import { Icono } from '../comunes/Icono';
import { SelectorCategorias } from './SelectorCategorias';

export interface FiltrosStockProps {
  filtros: Filtros;
  umbral: number;
  /** Nulo mientras cargan: el boton de categoria espera. */
  opciones: OpcionesFiltroProductos | null;
  onCambiar(filtros: Filtros): void;
}

export function FiltrosStock({ filtros, umbral, opciones, onCambiar }: FiltrosStockProps) {
  const [eligiendoCategorias, setEligiendoCategorias] = useState(false);

  const cambiar = (cambio: Partial<Filtros>) => {
    onCambiar(conCambio(filtros, cambio));
  };

  function buscar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const valor = new FormData(evento.currentTarget).get('termino');
    const texto = typeof valor === 'string' ? valor.trim() : '';
    cambiar({ termino: texto.length > 0 ? texto : undefined });
  }

  const estado = estadoDe(filtros, umbral);
  const cantidadCategorias = (filtros.lineas?.length ?? 0) + (filtros.categorias?.length ?? 0);

  return (
    <section className="filtros-tabla" aria-label="Filtros del stock">
      <form className="filtro filtro-buscar" role="search" onSubmit={buscar}>
        <label className="filtro-et" htmlFor="buscar-stock">
          Producto
        </label>
        <span className="filtro-buscar-campo">
          {/* `key`: si un chip quita la busqueda, el campo se vacia. */}
          <input
            key={filtros.termino ?? ''}
            id="buscar-stock"
            name="termino"
            type="search"
            className={filtros.termino !== undefined ? 'entrada activo' : 'entrada'}
            placeholder="Nombre o codigo"
            defaultValue={filtros.termino ?? ''}
          />
          <button type="submit" className="btn btn-sm btn-primario" aria-label="Buscar">
            <Icono nombre="buscar" />
          </button>
        </span>
      </form>

      <div className="filtro">
        <label className="filtro-et" htmlFor="filtro-estado-stock">
          Stock
        </label>
        <select
          id="filtro-estado-stock"
          className={estado !== null ? 'selector activo' : 'selector'}
          value={estado ?? ''}
          onChange={(evento) => {
            const elegido = evento.target.value as EstadoStock | '';
            cambiar(
              elegido === ''
                ? { stockMin: undefined, stockMax: undefined }
                : rangoDeEstado(elegido, umbral),
            );
          }}
        >
          <option value="">Todos</option>
          <option value="agotados">Agotados</option>
          <option value="por_agotarse">Por agotarse</option>
          <option value="con_stock">Con stock</option>
          {/* Un rango llegado por enlace se muestra, pero no se elige desde aca. */}
          {estado === 'rango' ? (
            <option value="rango" disabled>
              Otro rango
            </option>
          ) : null}
        </select>
      </div>

      <div className="filtro">
        <span className="filtro-et" id="filtro-categoria-stock-et">
          Categoria
        </span>
        <button
          type="button"
          className={cantidadCategorias > 0 ? 'btn-filtro activo' : 'btn-filtro'}
          aria-labelledby="filtro-categoria-stock-et filtro-categoria-stock-valor"
          disabled={opciones === null}
          onClick={() => {
            setEligiendoCategorias(true);
          }}
        >
          <span id="filtro-categoria-stock-valor">
            {cantidadCategorias > 0 ? `${cantidadCategorias} elegidas` : 'Todas'}
          </span>
          <Icono nombre="chevron" className="gira-abajo" />
        </button>
      </div>

      <div className="filtro filtro-orden">
        <label className="filtro-et" htmlFor="filtro-orden-stock">
          Ordenar por
        </label>
        <select
          id="filtro-orden-stock"
          className="selector"
          value={filtros.orden ?? ORDEN_STOCK_POR_DEFECTO}
          onChange={(evento) => {
            const orden = evento.target.value as OrdenProductosAdmin;
            cambiar({ orden: orden === ORDEN_STOCK_POR_DEFECTO ? undefined : orden });
          }}
        >
          {ORDENES_STOCK.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.texto}
            </option>
          ))}
        </select>
      </div>

      {eligiendoCategorias && opciones !== null ? (
        <SelectorCategorias
          lineas={opciones.lineas}
          lineasElegidas={filtros.lineas ?? []}
          categoriasElegidas={filtros.categorias ?? []}
          onCerrar={() => {
            setEligiendoCategorias(false);
          }}
          onAplicar={(lineas, categorias) => {
            setEligiendoCategorias(false);
            cambiar({ lineas, categorias });
          }}
        />
      ) : null}
    </section>
  );
}
